import { Controller } from "@hotwired/stimulus"
import { calculate } from "../modules/salary_lab/calculator_api"
import { formatCurrency, parseNumber } from "../modules/salary_lab/formatter"
import { SmartDebounce, detectEventType } from "../modules/salary_lab/smart_debounce"
import { writeGenericToUrl, readGenericFromUrl } from "../modules/salary_lab/url_state"
import { trackCalculatorUsed } from "../modules/salary_lab/ga4_tracker"

const URL_MAP = { target_net: "net", input_frequency: "freq", hours_per_week: "hrs", kiwisaver_perc: "ks", include_student_loan: "sl" }
const URL_TYPES = { target_net: "number", hours_per_week: "number", kiwisaver_perc: "number", include_student_loan: "boolean" }

export default class extends Controller {
  static targets = [
    "targetNetInput", "frequencySelect", "hoursField", "hoursInput",
    "kiwisaverSelect", "studentLoanCheckbox",
    "resultsPanel", "loadingIndicator",
    "requiredGross", "requiredContractorRate",
    "negotiationText",
    "annualGross", "monthlyGross", "fortnightlyGross", "weeklyGross", "hourlyGross",
    "breakdownNet", "breakdownTax", "breakdownAcc",
    "breakdownKiwisaver", "breakdownKiwisaverRow",
    "breakdownStudentLoan", "breakdownStudentLoanRow",
    "contractorHourly", "contractorDaily", "contractorAnnual", "contractorNet",
    "liveRegion"
  ]

  connect() {
    this._debounce = new SmartDebounce(() => this._performCalculation(), { minDigits: 4 })
    this._lastParams = null
    this._showHoursField()
    this._prefillFromUrl()
  }

  disconnect() {
    this._debounce.cancel()
  }

  inputChanged(event) {
    this._debounce.schedule(detectEventType(event), this.targetNetInputTarget.value)
  }

  frequencyChanged() {
    this._showHoursField()
    this._debounce.schedule("toggle", this.targetNetInputTarget.value)
  }

  optionChanged() {
    this._debounce.schedule("toggle", this.targetNetInputTarget.value)
  }

  async submitForm(event) {
    event.preventDefault()
    this._debounce.schedule("submit")
  }

  // --- Private ---

  _showHoursField() {
    if (!this.hasHoursFieldTarget || !this.hasFrequencySelectTarget) return
    const isHourly = this.frequencySelectTarget.value === "hourly"
    this.hoursFieldTarget.classList.toggle("hidden", !isHourly)
  }

  async _performCalculation() {
    const params = this._collectParams()
    if (!params.target_net || params.target_net <= 0) return

    const paramsKey = JSON.stringify(params)
    if (paramsKey === this._lastParams) return
    this._lastParams = paramsKey

    this._showLoading(true)

    try {
      const result = await calculate(params)
      this._renderResult(result)
      writeGenericToUrl(params, URL_MAP)
      trackCalculatorUsed("reverse", { salaryAmount: result.required_gross_salary, payFrequency: params.input_frequency })
    } catch (error) {
      if (this.hasLiveRegionTarget) {
        this.liveRegionTarget.textContent = error.message
      }
    } finally {
      this._showLoading(false)
    }
  }

  _collectParams() {
    return {
      type: "reverse",
      target_net: parseNumber(this.hasTargetNetInputTarget ? this.targetNetInputTarget.value : "0"),
      input_frequency: this.hasFrequencySelectTarget ? this.frequencySelectTarget.value : "annual",
      hours_per_week: this.hasHoursInputTarget ? parseFloat(this.hoursInputTarget.value) || 40 : 40,
      kiwisaver_perc: this.hasKiwisaverSelectTarget ? parseFloat(this.kiwisaverSelectTarget.value) || 0 : 0,
      include_student_loan: this.hasStudentLoanCheckboxTarget ? this.studentLoanCheckboxTarget.checked : false,
    }
  }

  _showLoading(show) {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.toggle("hidden", !show)
    }
  }

  _renderResult(result) {
    if (this.hasResultsPanelTarget) {
      this.resultsPanelTarget.classList.remove("hidden")
    }

    this._setText("requiredGross", formatCurrency(result.required_gross_salary))
    this._setText("requiredContractorRate", formatCurrency(result.required_contractor_rate, true))
    this._setText("negotiationText", result.negotiation_text)

    // Gross frequency table
    const freq = result.frequencies || {}
    if (freq.annual) this._setText("annualGross", formatCurrency(freq.annual.gross))
    if (freq.monthly) this._setText("monthlyGross", formatCurrency(freq.monthly.gross))
    if (freq.fortnightly) this._setText("fortnightlyGross", formatCurrency(freq.fortnightly.gross))
    if (freq.weekly) this._setText("weeklyGross", formatCurrency(freq.weekly.gross))
    if (freq.hourly) this._setText("hourlyGross", formatCurrency(freq.hourly.gross, true))

    // PAYE breakdown from full_breakdown
    const bd = result.full_breakdown
    if (bd) {
      this._setText("breakdownNet", formatCurrency(bd.net_salary))
      this._setText("breakdownTax", formatCurrency(bd.total_tax))
      this._setText("breakdownAcc", formatCurrency(bd.acc_levy))

      if (this.hasBreakdownKiwisaverRowTarget) {
        this.breakdownKiwisaverRowTarget.classList.toggle("hidden", bd.kiwisaver_employee <= 0)
      }
      this._setText("breakdownKiwisaver", formatCurrency(bd.kiwisaver_employee))

      if (this.hasBreakdownStudentLoanRowTarget) {
        this.breakdownStudentLoanRowTarget.classList.toggle("hidden", bd.student_loan <= 0)
      }
      this._setText("breakdownStudentLoan", formatCurrency(bd.student_loan))
    }

    // Contractor equivalent
    const cb = result.contractor_breakdown
    if (cb) {
      this._setText("contractorHourly", formatCurrency(cb.hourly_rate, true))
      this._setText("contractorDaily", formatCurrency(cb.daily_rate, true))
      this._setText("contractorAnnual", formatCurrency(cb.annual_gross))
      this._setText("contractorNet", formatCurrency(cb.net_take_home))
    }

    this._announceResult(result)
  }

  _prefillFromUrl() {
    const p = readGenericFromUrl(URL_MAP, URL_TYPES)
    if (!p.target_net) return
    this.targetNetInputTarget.value = p.target_net
    if (p.input_frequency && this.hasFrequencySelectTarget) { this.frequencySelectTarget.value = p.input_frequency; this._showHoursField() }
    if (p.hours_per_week != null && this.hasHoursInputTarget) this.hoursInputTarget.value = p.hours_per_week
    if (p.kiwisaver_perc != null && this.hasKiwisaverSelectTarget) this.kiwisaverSelectTarget.value = p.kiwisaver_perc
    if (p.include_student_loan && this.hasStudentLoanCheckboxTarget) this.studentLoanCheckboxTarget.checked = true
    if (this.element.dataset.prerendered !== "true") this._performCalculation()
  }

  _setText(targetName, text) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${capitalized}Target`]) {
      this[`${targetName}Target`].textContent = text
    }
  }

  _announceResult(result) {
    if (!this.hasLiveRegionTarget) return
    this.liveRegionTarget.textContent =
      `To take home ${formatCurrency(result.target_net)} per year, ` +
      `you need a gross salary of ${formatCurrency(result.required_gross_salary)}.`
  }
}
